import { useState } from "react";
import { z } from "zod";
import { Alert, AlertFilters, CreateAlert, ListingCondition } from "@snipe/core";
import { Button } from "./ui/button";
import { Checkbox, Field, Input, Select } from "./ui/field";

/**
 * The filter surface, which is Facebook's filter surface.
 *
 * Every control here maps onto one `browse_request_params` key (see
 * AlertFilters in @snipe/core), and @snipe/core's schema -- not a second copy of
 * the rules written in this file -- decides whether a draft is legal. That
 * matters more than it looks: a bound that moves in the shared package must
 * move this form in the same commit, and a form with its own opinion about
 * the maximum radius would submit values the server refuses.
 */

interface Draft {
  name: string;
  intervalSeconds: string;
  backfillDays: string;
  query: string;
  excludeKeywords: string;
  categoryIds: string;
  priceMin: string;
  priceMax: string;
  lat: string;
  lng: string;
  radiusKm: string;
  conditions: string[];
  localPickup: boolean;
  shipping: boolean;
  availableOnly: boolean;
}

const CONDITION_LABELS: Record<string, string> = {
  NEW: "New",
  USED_LIKE_NEW: "Used, like new",
  USED_GOOD: "Used, good",
  USED_FAIR: "Used, fair",
};

/** Zod speaks in field names; the person filling the form speaks in labels. */
const FIELD_LABELS: Record<string, string> = {
  priceMinMinor: "minimum price",
  priceMaxMinor: "maximum price",
  radiusKm: "radius",
  excludeKeywords: "excluded keywords",
  intervalSeconds: "check interval",
};

/** Which control an issue belongs under. */
const ISSUE_FIELD: Record<string, keyof Draft> = {
  query: "query",
  excludeKeywords: "excludeKeywords",
  categoryIds: "categoryIds",
  priceMinMinor: "priceMin",
  priceMaxMinor: "priceMax",
  lat: "lat",
  lng: "lng",
  radiusKm: "radiusKm",
  conditions: "conditions",
  name: "name",
  intervalSeconds: "intervalSeconds",
  backfillDays: "backfillDays",
};

function humanise(message: string): string {
  return Object.entries(FIELD_LABELS).reduce(
    (text, [key, label]) => text.replaceAll(key, label),
    message,
  );
}

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

/**
 * Pounds in, minor units out. `NaN` is deliberately passed through rather
 * than swallowed: zod rejects it with a message under the right field, where
 * a silent `null` would quietly drop the cap the customer just typed.
 */
function toMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return Math.round(Number(trimmed) * 100);
}

function toMajor(minor: number | null): string {
  return minor === null ? "" : String(minor / 100);
}

export function emptyDraft(): Draft {
  return {
    name: "",
    intervalSeconds: "300",
    backfillDays: "7",
    query: "",
    excludeKeywords: "",
    categoryIds: "",
    priceMin: "",
    priceMax: "",
    // Central London, because a radius with no centre is not a filter and an
    // empty pair of boxes is the slowest way to learn that.
    lat: "51.5074",
    lng: "-0.1278",
    radiusKm: "40",
    conditions: [],
    localPickup: true,
    shipping: true,
    availableOnly: true,
  };
}

export function draftFromAlert(alert: Alert): Draft {
  return {
    name: alert.name,
    intervalSeconds: String(alert.intervalSeconds),
    backfillDays: String(alert.backfillDays),
    query: alert.filters.query,
    excludeKeywords: alert.filters.excludeKeywords.join(", "),
    categoryIds: alert.filters.categoryIds.join(", "),
    priceMin: toMajor(alert.filters.priceMinMinor),
    priceMax: toMajor(alert.filters.priceMaxMinor),
    lat: String(alert.filters.lat),
    lng: String(alert.filters.lng),
    radiusKm: String(alert.filters.radiusKm),
    conditions: [...alert.filters.conditions],
    localPickup: alert.filters.localPickup,
    shipping: alert.filters.shipping,
    availableOnly: alert.filters.availableOnly,
  };
}

function numberOrNaN(value: string): number {
  return value.trim() === "" ? Number.NaN : Number(value);
}

/** Built from `Alert`'s own fields, so a bound that moves in @snipe/core moves here. */
const Meta = z.object({
  name: Alert.shape.name,
  intervalSeconds: Alert.shape.intervalSeconds,
  backfillDays: Alert.shape.backfillDays,
});

export interface AlertFormProps {
  initial: Draft;
  submitLabel: string;
  busy?: boolean | undefined;
  onSubmit: (input: CreateAlert) => void;
  onCancel?: (() => void) | undefined;
}

export function AlertForm({ initial, submitLabel, busy, onSubmit, onCancel }: AlertFormProps) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function submit() {
    const filters = AlertFilters.safeParse({
      query: draft.query,
      excludeKeywords: splitList(draft.excludeKeywords),
      categoryIds: splitList(draft.categoryIds),
      priceMinMinor: toMinor(draft.priceMin),
      priceMaxMinor: toMinor(draft.priceMax),
      lat: numberOrNaN(draft.lat),
      lng: numberOrNaN(draft.lng),
      radiusKm: numberOrNaN(draft.radiusKm),
      conditions: draft.conditions,
      localPickup: draft.localPickup,
      shipping: draft.shipping,
      availableOnly: draft.availableOnly,
    });

    // The three non-filter fields, checked against `Alert`'s own definitions
    // rather than numbers repeated here, and checked SEPARATELY from the
    // filters so that a nested failure arrives with a path this form can put
    // under a control instead of one `filters` issue with nowhere to go.
    const meta = Meta.safeParse({
      name: draft.name,
      intervalSeconds: numberOrNaN(draft.intervalSeconds),
      backfillDays: numberOrNaN(draft.backfillDays),
    });

    const collected: Partial<Record<keyof Draft, string>> = {};
    for (const issue of [
      ...(filters.success ? [] : filters.error.issues),
      ...(meta.success ? [] : meta.error.issues),
    ]) {
      const key = ISSUE_FIELD[String(issue.path[0] ?? "")];
      if (key !== undefined) collected[key] ??= humanise(issue.message);
    }

    if (!filters.success || !meta.success) {
      // Nothing is sent: an invalid draft is a sentence to fix, not a round
      // trip that comes back saying the same thing more slowly.
      setFormError(
        Object.keys(collected).length > 0 ? null : "The alert is not valid. Check the values above.",
      );
      setErrors(collected);
      return;
    }

    // The payload is validated as the API will validate it. If this ever
    // fails, the two schemas have diverged and saying so is far better than
    // posting a body the server will refuse.
    const input = CreateAlert.safeParse({ ...meta.data, filters: filters.data });
    if (!input.success) {
      setErrors({});
      setFormError(humanise(input.error.issues[0]?.message ?? "The alert is not valid."));
      return;
    }

    setErrors({});
    setFormError(null);
    onSubmit(input.data);
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Alert name" htmlFor="alert-name" error={errors.name}>
          <Input
            id="alert-name"
            value={draft.name}
            aria-invalid={errors.name !== undefined}
            onChange={(event) => set("name", event.target.value)}
          />
        </Field>

        <Field
          label="Search query"
          htmlFor="alert-query"
          hint="Sent to Facebook verbatim."
          error={errors.query}
        >
          <Input
            id="alert-query"
            value={draft.query}
            aria-invalid={errors.query !== undefined}
            onChange={(event) => set("query", event.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Exclude keywords"
        htmlFor="alert-exclude"
        hint="Comma separated. Applied to what Facebook sends back."
        error={errors.excludeKeywords}
      >
        <Input
          id="alert-exclude"
          value={draft.excludeKeywords}
          onChange={(event) => set("excludeKeywords", event.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Min price (£)" htmlFor="alert-price-min" error={errors.priceMin}>
          <Input
            id="alert-price-min"
            inputMode="decimal"
            value={draft.priceMin}
            placeholder="any"
            aria-invalid={errors.priceMin !== undefined}
            onChange={(event) => set("priceMin", event.target.value)}
          />
        </Field>
        <Field label="Max price (£)" htmlFor="alert-price-max" error={errors.priceMax}>
          <Input
            id="alert-price-max"
            inputMode="decimal"
            value={draft.priceMax}
            placeholder="any"
            aria-invalid={errors.priceMax !== undefined}
            onChange={(event) => set("priceMax", event.target.value)}
          />
        </Field>
        <Field label="Check every (seconds)" htmlFor="alert-interval" error={errors.intervalSeconds}>
          <Input
            id="alert-interval"
            inputMode="numeric"
            value={draft.intervalSeconds}
            aria-invalid={errors.intervalSeconds !== undefined}
            onChange={(event) => set("intervalSeconds", event.target.value)}
          />
        </Field>
        <Field
          label="Backfill (days)"
          htmlFor="alert-backfill"
          hint="Fetched once. No notifications."
          error={errors.backfillDays}
        >
          <Input
            id="alert-backfill"
            inputMode="numeric"
            value={draft.backfillDays}
            onChange={(event) => set("backfillDays", event.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Latitude" htmlFor="alert-lat" error={errors.lat}>
          <Input
            id="alert-lat"
            inputMode="decimal"
            value={draft.lat}
            aria-invalid={errors.lat !== undefined}
            onChange={(event) => set("lat", event.target.value)}
          />
        </Field>
        <Field label="Longitude" htmlFor="alert-lng" error={errors.lng}>
          <Input
            id="alert-lng"
            inputMode="decimal"
            value={draft.lng}
            aria-invalid={errors.lng !== undefined}
            onChange={(event) => set("lng", event.target.value)}
          />
        </Field>
        <Field label="Radius (km)" htmlFor="alert-radius" error={errors.radiusKm}>
          <Input
            id="alert-radius"
            inputMode="numeric"
            value={draft.radiusKm}
            aria-invalid={errors.radiusKm !== undefined}
            onChange={(event) => set("radiusKm", event.target.value)}
          />
        </Field>
        <Field
          label="Category ids"
          htmlFor="alert-categories"
          hint="Blank means every category."
          error={errors.categoryIds}
        >
          <Input
            id="alert-categories"
            value={draft.categoryIds}
            onChange={(event) => set("categoryIds", event.target.value)}
          />
        </Field>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-medium text-muted-foreground">
          Condition, or none for any
        </legend>
        <div className="flex flex-wrap gap-4">
          {ListingCondition.options.map((condition) => (
            <label key={condition} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.conditions.includes(condition)}
                onChange={(event) =>
                  set(
                    "conditions",
                    event.target.checked
                      ? [...draft.conditions, condition]
                      : draft.conditions.filter((value) => value !== condition),
                  )
                }
              />
              {CONDITION_LABELS[condition] ?? condition}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-medium text-muted-foreground">Delivery</legend>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.localPickup}
              onChange={(event) => set("localPickup", event.target.checked)}
            />
            Local pickup
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.shipping}
              onChange={(event) => set("shipping", event.target.checked)}
            />
            Shipping
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.availableOnly}
              onChange={(event) => set("availableOnly", event.target.checked)}
            />
            Hide sold
          </label>
        </div>
      </fieldset>

      {formError !== null ? (
        <p role="alert" className="text-sm font-medium text-danger">
          {formError}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={busy === true}>
          {submitLabel}
        </Button>
        {onCancel !== undefined ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

export type { Draft as AlertDraft };
